import axios, { AxiosError, AxiosHeaders } from 'axios';
import type { AxiosInstance, InternalAxiosRequestConfig } from 'axios';

import { apiConfig, TENANT_HEADER } from './apiConfig';

/**
 * Tenant-aware API client foundation.
 *
 * The axios instance lives outside React so it can be imported anywhere, while
 * the auth token and tenant id are injected at runtime by the corresponding
 * React providers via {@link setApiAuthToken} / {@link setApiTenantId}. This
 * keeps the HTTP layer decoupled from React but still tenant- and auth-aware.
 *
 * No real backend integration is performed yet — this is the wiring only.
 */

let currentAuthToken: string | null = null;
let currentTenantId: string | null = null;

/**
 * Register/refresh a bearer token attached to outgoing requests.
 *
 * @deprecated With the OAuth2-BFF (token-handler) model the browser never holds
 * tokens — auth rides on the `httpOnly` session cookie. Retained for backward
 * compatibility; passing a non-null token still attaches it but is not used in
 * production.
 */
export function setApiAuthToken(token: string | null): void {
  currentAuthToken = token;
}

/**
 * Register/refresh a tenant id attached to outgoing requests.
 *
 * @deprecated The active tenant is authoritative server-side (BFF session). Kept
 * for compatibility; the BFF ignores a client-supplied tenant header on
 * authenticated paths.
 */
export function setApiTenantId(tenantId: string | null): void {
  currentTenantId = tenantId;
}

const CSRF_COOKIE = 'XSRF-TOKEN';
const CSRF_HEADER = 'X-XSRF-TOKEN';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

function readCookie(name: string): string | null {
  if (typeof document === 'undefined') {
    return null;
  }
  const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

/** Normalised, transport-agnostic error shape consumed by the UI. */
export interface NormalizedApiError {
  /** HTTP status code, or null for network/client errors. */
  status: number | null;
  /** Machine-readable error code. */
  code: string;
  /** Human-readable message. */
  message: string;
  /** Optional structured details from the backend. */
  details?: unknown;
}

export const apiClient: AxiosInstance = axios.create({
  baseURL: apiConfig.baseURL,
  timeout: apiConfig.timeout,
  // Send the BFF session cookie on cross-subdomain (same-site) API calls.
  withCredentials: true,
  headers: {
    Accept: 'application/json',
    'Content-Type': 'application/json',
  },
});

// Attach the CSRF token (cookie → header) on state-changing requests, plus any
// legacy auth/tenant context that may have been registered.
apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const headers = AxiosHeaders.from(config.headers);

  // A FormData body (file upload) must let the browser inject its own
  // 'multipart/form-data; boundary=...' Content-Type. This instance sets a
  // 'Content-Type: application/json' default (below) for every other call; if that
  // header is still present when axios's transformRequest runs, it JSON-stringifies the
  // FormData instead of sending it as a multipart body, and the server rejects it with
  // HttpMediaTypeNotSupportedException (stage incident 2026-06-30, PUT
  // /api/v1/store/settings/media/logo — "Content-Type 'application/json' is not
  // supported"). Clearing it here, before transformRequest sees it, is what lets axios's
  // own FormData handling (resolveConfig.js) take over downstream and set the boundary.
  // A per-call `headers: { 'Content-Type': 'multipart/form-data' }` override (used by
  // some call sites for clarity) is harmless here — this clears it regardless.
  if (config.data instanceof FormData) {
    headers.delete('Content-Type');
  }

  const method = (config.method ?? 'get').toUpperCase();
  if (!SAFE_METHODS.has(method)) {
    const csrf = readCookie(CSRF_COOKIE);
    if (csrf) {
      headers.set(CSRF_HEADER, csrf);
    }
  }

  if (currentAuthToken) {
    headers.set('Authorization', `Bearer ${currentAuthToken}`);
  }
  if (currentTenantId) {
    headers.set(TENANT_HEADER, currentTenantId);
  }

  config.headers = headers;
  return config;
});

/**
 * Convert an unknown thrown value into a {@link NormalizedApiError}.
 * Acts as the single normalisation point for API failures.
 */
export function normalizeApiError(error: unknown): NormalizedApiError {
  if (axios.isAxiosError(error)) {
    const axiosError = error as AxiosError<{
      code?: string;
      message?: string;
      details?: unknown;
    }>;
    const data = axiosError.response?.data;

    return {
      status: axiosError.response?.status ?? null,
      code: data?.code ?? axiosError.code ?? 'UNKNOWN_ERROR',
      message: data?.message ?? axiosError.message ?? 'An unexpected error occurred.',
      details: data?.details,
    };
  }

  if (error instanceof Error) {
    return { status: null, code: 'UNKNOWN_ERROR', message: error.message };
  }

  return {
    status: null,
    code: 'UNKNOWN_ERROR',
    message: 'An unexpected error occurred.',
  };
}

// ── Response interceptor ─────────────────────────────────────────────────────
// On any 401 from an authenticated endpoint, the BFF session has expired
// (e.g. the server was restarted or the session idle limit was reached). Redirect
// the browser transparently to Keycloak. Because the Keycloak SSO session is
// typically still valid within the working day, the redirect returns without
// showing a login prompt and the user lands back at the page they were on.
//
// /api/v1/me is excluded: its 401 is the normal "not yet authenticated" probe
// that SessionContext handles to decide whether to show the login view.
const REAUTH_REDIRECT = '/oauth2/authorization/keycloak';
const REAUTH_RETURN_KEY = 'obzr:auth:return_path';

apiClient.interceptors.response.use(
  (response) => response,
  (error: unknown) => {
    if (axios.isAxiosError(error) && error.response?.status === 401) {
      const url = error.config?.url ?? '';
      if (!url.includes('/api/v1/me')) {
        // Persist the current SPA path so BffSessionProvider can restore it after
        // re-auth. Skip the welcome / root paths — there is nothing to restore there.
        const returnPath =
          window.location.pathname + window.location.search + window.location.hash;
        if (returnPath && returnPath !== '/' && !returnPath.startsWith('/welcome')) {
          sessionStorage.setItem(REAUTH_RETURN_KEY, returnPath);
        }
        window.location.assign(REAUTH_REDIRECT);
        // Return a never-resolving promise — navigation is already in progress.
        return new Promise(() => {});
      }
    }
    return Promise.reject(normalizeApiError(error));
  },
);

export { REAUTH_RETURN_KEY };
